/** Path length, altitude profile and clearance (spec §4.1 steps 9–10). */
import { dist, toWgs, type Frame } from './frame';
import type { Dtm, Profile, XY } from './types';

const PROFILE_STEP_M = 5;
const PROFILE_MAX_SAMPLES = 2000;

export function round(v: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
}

export function pathLength(points: XY[]): number {
  let s = 0;
  for (let i = 1; i < points.length; i++) s += dist(points[i - 1], points[i]);
  return s;
}

/**
 * Samples along the waypoint path, both series relative to the takeoff elevation: `flight_m` is
 * the height above takeoff, `terrain_m` the ground's height above takeoff (null without a DTM),
 * so `flight_m − terrain_m` is the clearance.
 */
export function buildProfile(wps: XY[], heights: number[], frame: Frame, dtm: Dtm | null, takeoffElev: number | null): Profile {
  const distance_m: number[] = [];
  const flight_m: number[] = [];
  const terrain_m: (number | null)[] = [];
  const total = pathLength(wps);
  const step = Math.max(PROFILE_STEP_M, total / PROFILE_MAX_SAMPLES);
  let travelled = 0;
  const push = (p: XY, h: number, d: number) => {
    distance_m.push(round(d, 1));
    flight_m.push(round(h, 1));
    if (dtm) {
      const t = dtm.sample(...toWgs(frame, p));
      terrain_m.push(t === null ? null : round(t - (takeoffElev ?? 0), 1));
    }
  };
  if (wps.length) push(wps[0], heights[0], 0);
  for (let i = 1; i < wps.length; i++) {
    const a = wps[i - 1];
    const b = wps[i];
    const seg = dist(a, b);
    for (let d = step; d < seg; d += step) {
      const t = d / seg;
      push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, heights[i - 1] + (heights[i] - heights[i - 1]) * t, travelled + d);
    }
    travelled += seg;
    push(b, heights[i], travelled);
  }
  return { distance_m, terrain_m: dtm ? terrain_m : null, flight_m };
}

export function clearance(profile: Profile): { min: number | null; max: number | null } {
  if (!profile.terrain_m) return { min: null, max: null };
  let min = Infinity;
  let max = -Infinity;
  profile.terrain_m.forEach((t, i) => {
    if (t === null) return;
    const c = profile.flight_m[i] - t;
    min = Math.min(min, c);
    max = Math.max(max, c);
  });
  return min === Infinity ? { min: null, max: null } : { min: round(min, 1), max: round(max, 1) };
}

/**
 * The longest exposure that keeps motion blur within one pixel: the drone may travel one GSD
 * while the shutter is open. Returned as the denominator of `1/x s`.
 */
export function shutterDenominator(speedMs: number, gsdCm: number): number {
  return Math.round(speedMs / (gsdCm / 100));
}
